import { FC, useEffect, useMemo, useRef, useState } from "react";
import { fetchAsync, postAsync } from "../services/ApiHandler";
import { useHeaderStore } from "../parts/CommonHeader";
import { FooterMenu } from "../components/FooterMenu";
import { Helmet } from "react-helmet";
import toast, { Toaster } from "react-hot-toast";
import { Loader, Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";
import { SubscriptionDialog } from "../parts/SubscriptionDialog";
import { SubscriptionDialogProps } from "../parts/SubscriptionDialog";
import { GenerationCreditDialog, GenerationCreditDialogProps } from "../parts/GenerationCreditDialog";
import { getNextPlanId } from "../services/SubscriptionService";
import { getSkyFlagUrl } from "../services/SkyFlagUtil";
import iconImageAdd from "../img/icon-image-add.png";
import iconAdjust from "../img/icon-adjust.png";
const VIDEO_PROMPT_DRAFT_KEY = "dropai:video-generation:prompt-draft";

type VideoMode = "lite" | "fast" | "standard";
type PlanTier = "free" | "lite" | "standard" | "advanced";
type DesktopVideoSettingKind = "resolution" | "duration" | "aspectRatio";
type MentionCandidate = {
  id: string;
  token: string;
  altToken: string;
  src?: string;
};
type FramePickerTarget = "first" | "last" | null;

type ReferenceImage = {
  id: string;
  name: string;
  base64: string;
};

type VideoTimelineSummary = {
  timelineId: string;
  TimelineId?: string;
  modelName: string;
  ModelName?: string;
  aspectRatio?: string;
  AspectRatio?: string;
  resolution?: string;
  Resolution?: string;
  durationSeconds?: number;
  DurationSeconds?: number;
  imageSize?: string;
  ImageSize?: string;
  thinkingLevel?: string;
  ThinkingLevel?: string;
  updatedDate?: string;
  UpdatedDate?: string;
  lastImagePath?: string;
  LastImagePath?: string;
  lastImageMimeType?: string;
  LastImageMimeType?: string;
  images?: GeminiTimelineImage[];
  Images?: GeminiTimelineImage[];
};

type GeminiTimelineImage = {
  imageId?: string;
  ImageId?: string;
  imageBase64?: string;
  ImageBase64?: string;
  imageMimeType?: string;
  ImageMimeType?: string;
  imagePath?: string;
  ImagePath?: string;
};

type GeminiTimelineMessage = {
  role?: string;
  Role?: string;
  text?: string;
  Text?: string;
  images?: GeminiTimelineImage[];
  Images?: GeminiTimelineImage[];
};

type GeminiTimelineDetailResponse = {
  code?: number;
  Code?: number;
  message?: string;
  Message?: string;
  timelineId?: string;
  TimelineId?: string;
  modelName?: string;
  ModelName?: string;
  aspectRatio?: string;
  AspectRatio?: string;
  resolution?: string;
  Resolution?: string;
  durationSeconds?: number;
  DurationSeconds?: number;
  imageSize?: string;
  ImageSize?: string;
  thinkingLevel?: string;
  ThinkingLevel?: string;
  messages?: GeminiTimelineMessage[];
  Messages?: GeminiTimelineMessage[];
};

type VeoOperationStatusResponse = {
  code?: number;
  Code?: number;
  message?: string;
  Message?: string;
  done?: boolean;
  Done?: boolean;
  errorMessage?: string;
  ErrorMessage?: string;
  videoPath?: string;
  VideoPath?: string;
  videoMimeType?: string;
  VideoMimeType?: string;
};

type VideoPromptIdea = {
  uid?: number;
  Uid?: number;
  name?: string;
  Name?: string;
  prompt?: string;
  Prompt?: string;
  displayOrder?: number;
  DisplayOrder?: number;
  isEnabled?: boolean;
  IsEnabled?: boolean;
};

const defaultVideoPromptIdeas: VideoPromptIdea[] = [
  { name: "製品のプロモーション動画", prompt: "製品のプロモーション動画" },
  { name: "モデル撮影風", prompt: "モデル撮影風" },
  { name: "TikTok風ショート動画", prompt: "TikTok風ショート動画" },
  { name: "モデルウォーキング", prompt: "モデルウォーキング" },
  { name: "擬人化", prompt: "擬人化" },
  { name: "車の運転", prompt: "車の運転" },
  { name: "犬がご飯を食べている", prompt: "犬がご飯を食べている" },
  { name: "電車を待ち合わせ", prompt: "電車を待ち合わせ" },
  { name: "ダンスチャレンジ", prompt: "ダンスチャレンジ" },
];

function getValue<T>(v1: T | undefined, v2: T | undefined): T | undefined {
  return v1 ?? v2;
}

function getCode(result: any): number {
  return result?.code ?? result?.Code ?? -1;
}

function getMessage(result: any): string {
  return result?.message ?? result?.Message ?? "エラーが発生しました";
}

function toMediaSrc(path?: string): string | undefined {
  if (!path) return undefined;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (!process.env.REACT_APP_IMAGE_URL) return undefined;
  return `${process.env.REACT_APP_IMAGE_URL}/${path}`;
}

function looksLikeVideoAsset(mimeType?: string, imagePath?: string, imageId?: string): boolean {
  const mime = (mimeType ?? "").toLowerCase();
  if (mime.startsWith("video/")) return true;
  const id = (imageId ?? "").toLowerCase();
  if (id.startsWith("tlvid_")) return true;
  const path = (imagePath ?? "").toLowerCase();
  if (path.includes("/tlvid_")) return true;
  return false;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("画像の読み込みに失敗しました"));
    reader.readAsDataURL(file);
  });
}

function modelToApiName(mode: VideoMode): string {
  if (mode === "standard") return "veo-3.1-standard";
  if (mode === "fast") return "veo-3.1-fast";
  return "veo-3.1-lite";
}

function modelToLabel(modelName?: string): string {
  const normalized = (modelName ?? "").toLowerCase();
  if (normalized.includes("veo-3.1-generate-preview") || normalized.includes("veo-3.1-standard")) return "Veo 3.1 Standard";
  if (normalized.includes("veo-3.1-fast") && !normalized.includes("fast-generate-preview")) return "Veo 3.1 Fast";
  if (normalized.includes("veo-3.1-lite")) return "Veo 3.1 Lite";
  // Backward compatibility
  if (normalized.includes("fast-generate-preview")) return "Veo 3.1 Lite";
  return "Veo 3.1 Lite";
}

function resolvePlanTierFromInternalProductId(internalProductId?: string): PlanTier {
  const value = (internalProductId ?? "").toLowerCase();
  if (value.startsWith("advanced")) return "advanced";
  if (value.startsWith("standard")) return "standard";
  if (value.startsWith("lite")) return "lite";
  return "free";
}

function getVeoPerSecondCost(planTier: PlanTier, mode: VideoMode, resolution: "720p" | "1080p" | "4k"): number | null {
  if (mode === "lite") {
    if (resolution === "4k") return null;
    if (planTier === "free") return 44;
    if (planTier === "lite") return 41;
    if (planTier === "standard") return 39;
    return 38;
  }

  if (mode === "fast") {
    if (resolution === "4k") {
      if (planTier === "standard") return 177;
      if (planTier === "advanced") return 164;
      return null;
    }
    if (planTier === "standard") return 77;
    if (planTier === "advanced") return 71;
    return null;
  }

  if (resolution === "4k") {
    if (planTier === "standard") return 299;
    if (planTier === "advanced") return 284;
    return null;
  }
  if (planTier === "standard") return 193;
  if (planTier === "advanced") return 190;
  return null;
}

function canUseVideoMode(planTier: PlanTier, mode: VideoMode): boolean {
  if (mode === "lite") return true;
  if (mode === "fast") return planTier === "standard" || planTier === "advanced";
  return planTier === "standard" || planTier === "advanced"; // Veo 3.1 Standard
}

function canUseResolution(planTier: PlanTier, mode: VideoMode, resolution: "720p" | "1080p" | "4k"): boolean {
  if (resolution !== "4k") return true;
  if (mode === "lite") return false;
  if (mode === "fast") return planTier === "standard" || planTier === "advanced";
  return planTier === "advanced";
}

export const VideoGenerationPage: FC = () => {
  const navigate = useNavigate();
  const { isLoggedIn, fetchActiveSubscription, fetchRemainCount, subscription, screenWidth } = useHeaderStore();

  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState<VideoMode>("lite");
  const [aspectRatio, setAspectRatio] = useState<"16:9" | "9:16">("16:9");
  const [resolution, setResolution] = useState<"720p" | "1080p" | "4k">("720p");
  const [durationSeconds, setDurationSeconds] = useState<4 | 6 | 8>(8);
  const [references, setReferences] = useState<ReferenceImage[]>([]);
  const [firstFrameId, setFirstFrameId] = useState<string>("");
  const [lastFrameId, setLastFrameId] = useState<string>("");
  const [history, setHistory] = useState<VideoTimelineSummary[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [currentTimelineId, setCurrentTimelineId] = useState<string>("");

  const [selectedSummary, setSelectedSummary] = useState<VideoTimelineSummary | undefined>(undefined);
  const [generatedVideoSrc, setGeneratedVideoSrc] = useState<string | undefined>(undefined);
  const [requestImages, setRequestImages] = useState<GeminiTimelineImage[]>([]);
  const [selectedPromptText, setSelectedPromptText] = useState<string>("");
  const [videoDialogOpened, setVideoDialogOpened] = useState(false);
  const [videoDialogTab, setVideoDialogTab] = useState<"basic" | "timeline">("basic");
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);
  const [isDeletingTimeline, setIsDeletingTimeline] = useState(false);
  const [subscriptionDialogProps, setSubscriptionDialogProps] = useState<SubscriptionDialogProps | undefined>(undefined);
  const [subscriptionDialogOpened, { open: openSubscriptionDialog, close: closeSubscriptionDialog }] = useDisclosure(false);
  const [generationCreditDialogProps, setGenerationCreditDialogProps] = useState<GenerationCreditDialogProps | undefined>(undefined);
  const [creditDialogOpened, { open: openCreditDialog, close: closeCreditDialog }] = useDisclosure(false);
  const [generatingWaitModalOpened, { open: openGeneratingWaitModal, close: closeGeneratingWaitModal }] = useDisclosure(false);
  const [videoSettingsVisible, setVideoSettingsVisible] = useState(false);
  const [modelSelectorOpened, setModelSelectorOpened] = useState(false);
  const [desktopSettingPicker, setDesktopSettingPicker] = useState<DesktopVideoSettingKind | null>(null);
  const [framePickerTarget, setFramePickerTarget] = useState<FramePickerTarget>(null);
  const [promptInputMinHeight, setPromptInputMinHeight] = useState(120);
  const [promptResizeState, setPromptResizeState] = useState<{ startY: number; startHeight: number } | null>(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const [mentionPopupPos, setMentionPopupPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const [mentionButtonOpened, setMentionButtonOpened] = useState(false);
  const [promptIdeas, setPromptIdeas] = useState<VideoPromptIdea[]>(defaultVideoPromptIdeas);
  const pollTimerRef = useRef<number | undefined>(undefined);
  const referenceInputRef = useRef<HTMLInputElement | null>(null);
  const firstFrameInputRef = useRef<HTMLInputElement | null>(null);
  const lastFrameInputRef = useRef<HTMLInputElement | null>(null);
  const promptTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const promptHighlightRef = useRef<HTMLDivElement | null>(null);
  const promptInputWrapRef = useRef<HTMLDivElement | null>(null);
  const mentionButtonWrapRef = useRef<HTMLDivElement | null>(null);
  const firstFrameWrapRef = useRef<HTMLDivElement | null>(null);
  const lastFrameWrapRef = useRef<HTMLDivElement | null>(null);

  const selectedFirstImage = useMemo(() => references.find((e) => e.id === firstFrameId), [references, firstFrameId]);
  const selectedLastImage = useMemo(() => references.find((e) => e.id === lastFrameId), [references, lastFrameId]);
  const mentionCandidates = useMemo<MentionCandidate[]>(
    () =>
      references.map((ref, idx) => ({
        id: ref.id,
        token: `画像${idx + 1}`,
        altToken: `Image${idx + 1}`,
        src: ref.base64,
      })),
    [references]
  );
  const filteredMentionCandidates = useMemo(() => {
    if (mentionStart == null) return [];
    const q = mentionQuery.trim().toLowerCase();
    if (!q) return mentionCandidates;
    return mentionCandidates.filter((c) =>
      c.token.toLowerCase().includes(q) || c.altToken.toLowerCase().includes(q)
    );
  }, [mentionCandidates, mentionQuery, mentionStart]);
  const frameCandidates = useMemo(
    () =>
      references.map((ref, idx) => ({
        id: ref.id,
        token: `画像${idx + 1}`,
        src: ref.base64,
      })),
    [references]
  );
  const mentionedReferenceIds = useMemo(() => {
    const ids = new Set<string>();
    for (let i = 0; i < references.length; i++) {
      const ref = references[i];
      const token = `@Image${i + 1}`;
      const altToken = `@画像${i + 1}`;
      if ((ref.name && prompt.includes(`@${ref.name}`)) || prompt.includes(token) || prompt.includes(altToken)) {
        ids.add(ref.id);
      }
    }
    return ids;
  }, [prompt, references]);
  const referenceImagesForApi = useMemo(
    () => references.filter((e) =>
      e.id !== firstFrameId &&
      e.id !== lastFrameId &&
      mentionedReferenceIds.has(e.id)
    ),
    [references, firstFrameId, lastFrameId, mentionedReferenceIds]
  );
  const hasReferenceImagesForApi = referenceImagesForApi.length > 0;
  const requiresFixedEightSeconds = resolution !== "720p" || !!lastFrameId || hasReferenceImagesForApi;
  const effectiveDuration = requiresFixedEightSeconds ? 8 : durationSeconds;
  const currentPlanTier = resolvePlanTierFromInternalProductId(subscription?.internalProductId);
  const currentPerSecondCost = getVeoPerSecondCost(currentPlanTier, mode, resolution);
  const currentConsumingCredit = currentPerSecondCost == null ? null : currentPerSecondCost * effectiveDuration;
  const canUseCurrentMode = canUseVideoMode(currentPlanTier, mode);
  const canUseCurrentResolution = canUseResolution(currentPlanTier, mode, resolution);
  const availableDurationOptions = useMemo(() => (requiresFixedEightSeconds ? [8] : [4, 6, 8]), [requiresFixedEightSeconds]);
  const availableResolutionOptions = useMemo<("720p" | "1080p" | "4k")[]>(
    () => (mode === "lite" ? ["720p", "1080p"] : ["720p", "1080p", "4k"]),
    [mode]
  );

  useEffect(() => {
    if (!promptResizeState) return;

    const onMouseMove = (event: MouseEvent) => {
      const nextHeight = Math.max(120, Math.min(720, promptResizeState.startHeight + (event.clientY - promptResizeState.startY)));
      setPromptInputMinHeight(nextHeight);
    };
    const onMouseUp = () => setPromptResizeState(null);

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [promptResizeState]);

  function syncPromptHighlightScroll() {
    if (!promptTextareaRef.current || !promptHighlightRef.current) return;
    promptHighlightRef.current.scrollTop = promptTextareaRef.current.scrollTop;
    promptHighlightRef.current.scrollLeft = promptTextareaRef.current.scrollLeft;
  }

  function detectMentionAtCaret(value: string, caretPos: number) {
    const before = value.slice(0, caretPos);
    const match = before.match(/(^|\s)@([^\s@]*)$/);
    if (!match) return null;
    const query = match[2] ?? "";
    const start = before.length - query.length - 1;
    return { start, query };
  }

  function getTextareaCaretPosition(textarea: HTMLTextAreaElement, position: number) {
    const style = window.getComputedStyle(textarea);
    const div = document.createElement("div");
    const copyProps = [
      "boxSizing", "width", "height", "overflowX", "overflowY",
      "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth",
      "paddingTop", "paddingRight", "paddingBottom", "paddingLeft",
      "fontStyle", "fontVariant", "fontWeight", "fontStretch", "fontSize", "fontSizeAdjust",
      "lineHeight", "fontFamily", "textAlign", "textTransform", "textIndent", "textDecoration",
      "letterSpacing", "wordSpacing", "tabSize", "MozTabSize"
    ] as const;
    copyProps.forEach((prop) => {
      // @ts-ignore
      div.style[prop] = style[prop];
    });

    div.style.position = "absolute";
    div.style.visibility = "hidden";
    div.style.whiteSpace = "pre-wrap";
    div.style.wordWrap = "break-word";
    div.style.pointerEvents = "none";

    const before = textarea.value.substring(0, position);
    const after = textarea.value.substring(position) || ".";
    div.textContent = before;

    const span = document.createElement("span");
    span.textContent = after[0];
    div.appendChild(span);

    document.body.appendChild(div);
    const left = span.offsetLeft;
    const top = span.offsetTop;
    const lineHeight = parseFloat(style.lineHeight || "28") || 28;
    document.body.removeChild(div);

    return { left: left - textarea.scrollLeft, top: top - textarea.scrollTop, lineHeight };
  }

  function updateMentionPopupPosition(caretPos: number) {
    const textarea = promptTextareaRef.current;
    const wrap = promptInputWrapRef.current;
    if (!textarea || !wrap) return;
    const caret = getTextareaCaretPosition(textarea, caretPos);
    const maxLeft = Math.max(0, wrap.clientWidth - 250);
    setMentionPopupPos({
      left: Math.min(maxLeft, Math.max(0, caret.left)),
      top: Math.max(0, caret.top + caret.lineHeight + 6),
    });
  }

  function updateMentionState(value: string, caretPos: number) {
    const mention = detectMentionAtCaret(value, caretPos);
    if (mention && mentionCandidates.length > 0) {
      const nextQuery = mention.query;
      const nextStart = mention.start;
      const queryChanged = mentionStart !== nextStart || mentionQuery !== nextQuery;
      setMentionStart(nextStart);
      setMentionQuery(nextQuery);
      if (queryChanged) {
        setMentionActiveIndex(0);
      }
      updateMentionPopupPosition(caretPos);
    } else {
      closeMentionSuggest();
    }
  }

  function closeMentionSuggest() {
    setMentionStart(null);
    setMentionQuery("");
    setMentionActiveIndex(0);
  }

  function insertMention(token: string, fromButton: boolean = false) {
    if (!promptTextareaRef.current) return;
    const ta = promptTextareaRef.current;
    const caretPos = ta.selectionStart ?? prompt.length;
    const insertStart = fromButton
      ? caretPos
      : (mentionStart == null ? caretPos : mentionStart);
    const before = prompt.slice(0, insertStart);
    const after = prompt.slice(caretPos);
    const next = `${before}@${token} ${after}`;
    const nextCaret = (before + `@${token} `).length;
    setPrompt(next);
    closeMentionSuggest();
    setMentionButtonOpened(false);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(nextCaret, nextCaret);
      syncPromptHighlightScroll();
      updateMentionPopupPosition(nextCaret);
    });
  }

  function renderPromptHighlighted(text: string) {
    if (!text) return <span>&nbsp;</span>;
    const mentionSet = new Set<string>();
    mentionCandidates.forEach((c) => {
      mentionSet.add(`@${c.token}`);
      mentionSet.add(`@${c.altToken}`);
    });
    const parts = text.split(/(@[A-Za-z0-9_一-龠ぁ-んァ-ヶー]+)/g);
    return parts.map((part, idx) => {
      const isMention = mentionSet.has(part);
      if (!isMention) return <span key={`prompt-part-${idx}`}>{part}</span>;
      return (
        <span key={`prompt-part-${idx}`} className="bg-[#E9ECFF] text-[#27307A] rounded px-[1px]">
          {part}
        </span>
      );
    });
  }

  function getVideoModelLabel(value: VideoMode) {
    if (value === "standard") return "Veo 3.1 Standard";
    if (value === "fast") return "Veo 3.1 Fast";
    return "Veo 3.1 Lite";
  }

  function buildVideoModelSelector(isMobile: boolean) {
    const label = getVideoModelLabel(mode);
    const buttonClass = isMobile
      ? "h-8 max-w-[170px] min-w-0 px-1.5 rounded-xl border border-[#D9DCEF] bg-white inline-flex items-center justify-between text-[#11175B] font-bold text-[10px]"
      : "h-10 min-w-[240px] px-3 rounded-2xl border border-[#D9DCEF] bg-white inline-flex items-center justify-between text-[#11175B] font-bold text-sm";
    const iconStyle = isMobile ? { width: "12px", height: "12px" } : { width: "18px", height: "18px" };

    return (
      <button
        type="button"
        onClick={() => setModelSelectorOpened(true)}
        className={buttonClass}
      >
        <span className="inline-flex items-center gap-1 min-w-0 ml-0.5">
          <img
            src="/images/icon/google.jpg"
            alt="Google"
            className="shrink-0 object-contain"
            style={iconStyle}
          />
          <span className="whitespace-nowrap truncate max-w-[150px]">{label}</span>
        </span>
        <span className="ml-1 text-[#5B63A8] shrink-0 text-[11px]">{">"}</span>
      </button>
    );
  }

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchActiveSubscription();
    fetchRemainCount();
    fetchHistory();
  }, [isLoggedIn]);

  useEffect(() => {
    try {
      const savedPrompt = window.sessionStorage.getItem(VIDEO_PROMPT_DRAFT_KEY);
      if (savedPrompt !== null) {
        setPrompt(savedPrompt);
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  useEffect(() => {
    fetchVideoPromptIdeaList();
  }, []);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(VIDEO_PROMPT_DRAFT_KEY, prompt);
    } catch {
      // ignore storage errors
    }
  }, [prompt]);

  useEffect(() => {
    if (resolution !== "720p" && durationSeconds !== 8) {
      setDurationSeconds(8);
    }
  }, [resolution, durationSeconds]);

  useEffect(() => {
    if (!canUseVideoMode(currentPlanTier, mode)) {
      setMode("lite");
    }
  }, [currentPlanTier, mode]);

  useEffect(() => {
    if (!canUseResolution(currentPlanTier, mode, resolution)) {
      setResolution("720p");
    }
  }, [currentPlanTier, mode, resolution]);

  useEffect(() => {
    if (mentionStart != null && filteredMentionCandidates.length === 0) {
      closeMentionSuggest();
    }
  }, [mentionStart, filteredMentionCandidates.length]);

  useEffect(() => {
    function onMouseDownOutsideMentionButton(event: MouseEvent) {
      if (!mentionButtonOpened) return;
      const target = event.target as Node;
      const mentionWrap = mentionButtonWrapRef.current;
      if (mentionWrap && !mentionWrap.contains(target)) {
        setMentionButtonOpened(false);
      }
    }
    document.addEventListener("mousedown", onMouseDownOutsideMentionButton);
    return () => document.removeEventListener("mousedown", onMouseDownOutsideMentionButton);
  }, [mentionButtonOpened]);

  useEffect(() => {
    if (references.length === 0) {
      setFramePickerTarget(null);
    }
  }, [references.length]);

  useEffect(() => {
    function onMouseDownOutside(event: MouseEvent) {
      if (framePickerTarget == null) return;
      const target = event.target as Node;
      const firstWrap = firstFrameWrapRef.current;
      const lastWrap = lastFrameWrapRef.current;
      const clickedInsideFirst = !!firstWrap && firstWrap.contains(target);
      const clickedInsideLast = !!lastWrap && lastWrap.contains(target);
      if (!clickedInsideFirst && !clickedInsideLast) {
        setFramePickerTarget(null);
      }
    }
    document.addEventListener("mousedown", onMouseDownOutside);
    return () => document.removeEventListener("mousedown", onMouseDownOutside);
  }, [framePickerTarget]);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const toastId = "running-generation-toast";
    const totalRunningCount = processing ? 1 : 0;

    if (totalRunningCount > 0) {
      toast(
        <div className="flex items-center gap-2">
          <img
            src={`${process.env.PUBLIC_URL ?? ""}/images/icon-timer.png`}
            alt="timer"
            className="w-4 h-4 object-contain"
          />
          <span className="whitespace-pre-line">{`現在${totalRunningCount}件の生成を実行中。\n完了までお待ちください。`}</span>
        </div>,
        {
          id: toastId,
          duration: Infinity,
          position: "bottom-right",
          style: {
            background: "#FFFFFF",
            color: "#1F2352",
            border: "1px solid #D9DCEF",
            fontSize: "13px"
          }
        }
      );
      return;
    }

    toast.dismiss(toastId);
  }, [processing]);

  async function fetchHistory() {
    if (!isLoggedIn) return;

    setLoadingHistory(true);
    try {
      const list = await fetchAsync<VideoTimelineSummary[]>("Generation/GetVeoTimelineList", { page: 0, count: 30, asc: false });
      setHistory(list ?? []);
    } catch {
      toast.error("動画履歴の取得に失敗しました");
    } finally {
      setLoadingHistory(false);
    }
  }

  async function fetchVideoPromptIdeaList() {
    try {
      const list = await fetchAsync<VideoPromptIdea[]>("VideoPromptIdea/GetList");
      if (!Array.isArray(list) || list.length === 0) {
        return;
      }
      const normalized = list
        .filter((item) => (getValue(item.isEnabled, item.IsEnabled) ?? true) === true)
        .map((item) => ({
          ...item,
          uid: getValue(item.uid, item.Uid),
          name: getValue(item.name, item.Name),
          prompt: getValue(item.prompt, item.Prompt),
          displayOrder: getValue(item.displayOrder, item.DisplayOrder),
        }))
        .filter((item) => !!item.name && !!item.prompt);

      if (normalized.length > 0) {
        setPromptIdeas(normalized);
      }
    } catch {
      // defaultVideoPromptIdeas をフォールバック表示
    }
  }

  async function onSelectHistory(item: VideoTimelineSummary, openTimelineTab: boolean = true) {
    const timelineId = getValue(item.timelineId, item.TimelineId);
    if (!timelineId) return;

    try {
      const detail = await fetchAsync<GeminiTimelineDetailResponse>("Generation/GetGeminiTimelineDetail", { timelineId, includeImageBase64: false });
      const code = getCode(detail);
      if (code !== 1) {
        toast.error(getMessage(detail));
        return;
      }

      setSelectedSummary({
        ...item,
        aspectRatio: getValue(detail.aspectRatio, detail.AspectRatio) ?? getValue(item.aspectRatio, item.AspectRatio),
        resolution: getValue(detail.resolution, detail.Resolution) ?? getValue(item.resolution, item.Resolution) ?? getValue(detail.imageSize, detail.ImageSize) ?? getValue(item.imageSize, item.ImageSize),
        durationSeconds: getValue(detail.durationSeconds, detail.DurationSeconds) ?? getValue(item.durationSeconds, item.DurationSeconds),
        imageSize: getValue(detail.imageSize, detail.ImageSize) ?? getValue(item.imageSize, item.ImageSize),
        thinkingLevel: getValue(detail.thinkingLevel, detail.ThinkingLevel) ?? getValue(item.thinkingLevel, item.ThinkingLevel),
        modelName: getValue(detail.modelName, detail.ModelName) ?? getValue(item.modelName, item.ModelName) ?? "",
      });

      const messages = getValue(detail.messages, detail.Messages) ?? [];
      let latestRequestImageList: GeminiTimelineImage[] = [];
      let latestVideoSrc: string | undefined = undefined;
      let latestUserText = "";
      messages.forEach((msg) => {
        const role = getValue(msg.role, msg.Role) ?? "";
        const messageText = getValue(msg.text, msg.Text);
        const images = getValue(msg.images, msg.Images) ?? [];
        const currentUserImages: GeminiTimelineImage[] = [];
        images.forEach((img) => {
          const mime = getValue(img.imageMimeType, img.ImageMimeType) ?? "";
          const path = getValue(img.imagePath, img.ImagePath);
          const imageId = getValue(img.imageId, img.ImageId);
          if (role === "user" && mime.startsWith("image/") && !looksLikeVideoAsset(mime, path, imageId)) {
            currentUserImages.push(img);
          }
          if (looksLikeVideoAsset(mime, path, imageId)) {
            latestVideoSrc = toMediaSrc(path);
          }
        });
        if (role === "user") {
          const hasText = !!(messageText && messageText.trim() !== "");
          if (hasText) {
            latestUserText = messageText ?? "";
          }
          if (hasText || currentUserImages.length > 0) {
            latestRequestImageList = currentUserImages;
          }
        }
      });

      setRequestImages(latestRequestImageList);
      setSelectedPromptText(latestUserText);
      const summaryMime = getValue(item.lastImageMimeType, item.LastImageMimeType);
      const summaryPath = getValue(item.lastImagePath, item.LastImagePath);
      const fallbackVideoSrc = looksLikeVideoAsset(summaryMime, summaryPath) ? toMediaSrc(summaryPath) : undefined;
      setGeneratedVideoSrc(latestVideoSrc ?? fallbackVideoSrc);
      setCurrentTimelineId(timelineId);
      setVideoDialogTab(openTimelineTab ? "timeline" : "basic");
      setVideoDialogOpened(true);
    } catch {
      toast.error("動画詳細の取得に失敗しました");
    }
  }

  async function onUploadReferenceImages(files: FileList | null) {
    if (!files || files.length === 0) return;

    try {
      const newItems: ReferenceImage[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files.item(i);
        if (!file) continue;
        const base64 = await readFileAsDataUrl(file);
        newItems.push({
          id: `${Date.now()}_${i}_${Math.random().toString(16).slice(2)}`,
          name: `画像${references.length + newItems.length + 1}`,
          base64,
        });
      }
      setReferences((prev) => [...prev, ...newItems]);
    } catch {
      toast.error("参照画像の読み込みに失敗しました");
    }
  }

  async function onUploadFrameImage(files: FileList | null, target: "first" | "last") {
    if (!files || files.length === 0) return;

    const file = files.item(0);
    if (!file) return;

    try {
      const base64 = await readFileAsDataUrl(file);
      const nextId = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
      const nextItem: ReferenceImage = {
        id: nextId,
        name: target === "first" ? `最初フレーム${references.length + 1}` : `最後フレーム${references.length + 1}`,
        base64,
      };
      setReferences((prev) => [...prev, nextItem]);
      if (target === "first") {
        setFirstFrameId(nextId);
      } else {
        setLastFrameId(nextId);
      }
    } catch {
      toast.error(target === "first" ? "最初フレーム画像の読み込みに失敗しました" : "最後フレーム画像の読み込みに失敗しました");
    }
  }

  function removeReference(id: string) {
    setReferences((prev) => prev.filter((e) => e.id !== id));
    if (firstFrameId === id) setFirstFrameId("");
    if (lastFrameId === id) setLastFrameId("");
  }

  function buildSelectedReferenceImageRow() {
    if (references.length === 0) return null;

    return (
      <div className="mt-2 flex items-center gap-2 overflow-x-auto pb-1">
        {references.map((ref, index) => (
          <div
            key={`selected-reference-image-${ref.id}`}
            className="relative shrink-0 w-8 h-8 rounded-lg overflow-hidden border border-[#D7DCEF] bg-white"
            title={ref.name}
          >
            <img src={ref.base64} alt={`参照画像${index + 1}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeReference(ref.id);
              }}
              className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-white border border-gray-300 flex items-center justify-center"
              aria-label="参照画像を削除"
            >
              <img src="/images/icon-close.svg" alt="削除" className="w-2 h-2" />
            </button>
          </div>
        ))}
      </div>
    );
  }

  async function startPollingOperation(timelineId: string, operationName: string) {
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = undefined;
    }

    try {
      const status = await fetchAsync<VeoOperationStatusResponse>("Generation/GetVeoOperation", { timelineId, operationName });
      const code = getCode(status);
      const done = getValue(status.done, status.Done) === true;

      if (code !== 1) {
        setProcessing(false);
        toast.error(getMessage(status));
        await fetchHistory();
        return;
      }

      if (!done) {
        pollTimerRef.current = window.setTimeout(() => {
          startPollingOperation(timelineId, operationName);
        }, 5000);
        return;
      }

      const errorMessage = getValue(status.errorMessage, status.ErrorMessage);
      if (errorMessage) {
        setProcessing(false);
        toast.error(errorMessage);
        await fetchHistory();
        return;
      }

      const videoPath = getValue(status.videoPath, status.VideoPath);
      setGeneratedVideoSrc(toMediaSrc(videoPath));
      setProcessing(false);
      toast.success("動画生成が完了しました");
      await fetchRemainCount();
      await fetchHistory();

      const matched = history.find((e) => getValue(e.timelineId, e.TimelineId) === timelineId);
      if (matched) {
        await onSelectHistory(matched, true);
      }
    } catch {
      setProcessing(false);
      toast.error("動画生成の状態取得に失敗しました");
    }
  }

  async function onGenerateVideo() {
    if (processing) {
      openGeneratingWaitModal();
      return;
    }

    if (!isLoggedIn) {
      toast.error("動画生成はログイン後に利用できます");
      return;
    }

    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) {
      toast.error("動画プロンプトを入力してください");
      return;
    }
    if (!canUseCurrentMode) {
      toast.error("現在のプランではこの動画モデルは利用できません");
      return;
    }
    if (!canUseCurrentResolution) {
      toast.error("現在のプランではこの解像度は利用できません");
      return;
    }
    if (lastFrameId && !firstFrameId) {
      toast.error("最後のフレームを使う場合は、最初のフレームも設定してください");
      return;
    }

    const adjustedDuration = (resolution !== "720p" || !!lastFrameId || hasReferenceImagesForApi) ? 8 : durationSeconds;

    if (adjustedDuration !== durationSeconds) {
      setDurationSeconds(8);
      toast("この条件では8秒固定で生成します");
    }

    setProcessing(true);
    setGeneratedVideoSrc(undefined);
    setRequestImages([]);
    setSelectedPromptText(trimmedPrompt);
    setVideoDialogTab("timeline");
    setVideoDialogOpened(true);
    setSelectedSummary({
      timelineId: currentTimelineId || "",
      modelName: modelToApiName(mode),
      aspectRatio,
      resolution,
      durationSeconds: adjustedDuration,
      updatedDate: new Date().toISOString(),
    });

    try {
      const result = await postAsync<any>("Generation/GenerateVeoVideo", {
        timelineId: currentTimelineId || undefined,
        modelName: modelToApiName(mode),
        prompt: trimmedPrompt,
        aspectRatio,
        resolution,
        durationSeconds: adjustedDuration,
        firstFrameImageBase64: selectedFirstImage?.base64,
        lastFrameImageBase64: selectedLastImage?.base64,
        referenceImageBase64List: referenceImagesForApi.map((e) => e.base64),
      });
      const raw = result as any;

      const code = getCode(raw);
      if (code !== 1) {
        setProcessing(false);
        if (code === -2) {
          setVideoDialogOpened(false);
          toast.error("クレジットが足りません。");
          if (!subscription?.internalProductId?.startsWith("standard")) {
            const nextPlanId = getNextPlanId(subscription);
            if (!nextPlanId) {
              return;
            }
            setSubscriptionDialogProps({
              planId: nextPlanId,
              isTrial: true
            } as SubscriptionDialogProps);
            openSubscriptionDialog();
          }
          return;
        }
        toast.error(getMessage(raw));
        return;
      }

      const timelineId = getValue(raw.timelineId, raw.TimelineId);
      const operationName = getValue(raw.operationName, raw.OperationName);
      if (!timelineId || !operationName) {
        setProcessing(false);
        toast.error("動画生成の開始に失敗しました");
        return;
      }

      setCurrentTimelineId(timelineId);
      await startPollingOperation(timelineId, operationName);
    } catch {
      setProcessing(false);
      toast.error("動画生成に失敗しました");
    }
  }

  function openGenerationCreditModal() {
    setGenerationCreditDialogProps({
      onPurchase: () => {
        setGenerationCreditDialogProps(undefined);
      },
      onCancel: () => {
        setGenerationCreditDialogProps(undefined);
      }
    } as GenerationCreditDialogProps);
    openCreditDialog();
  }

  const desktopPreviewItems = useMemo(() => {
    return history
      .map((item) => {
        const timelineId = getValue(item.timelineId, item.TimelineId) ?? "";
        const allImages = (getValue(item.images, item.Images) ?? []) as GeminiTimelineImage[];
        const preferredImage = allImages.find((img) => {
          const mime = getValue(img.imageMimeType, img.ImageMimeType);
          const path = getValue(img.imagePath, img.ImagePath);
          const imageId = getValue(img.imageId, img.ImageId);
          return !!path && !!mime && mime.startsWith("image/") && !looksLikeVideoAsset(mime, path, imageId);
        });

        const selectedPath = getValue(preferredImage?.imagePath, preferredImage?.ImagePath) ?? getValue(item.lastImagePath, item.LastImagePath);
        const selectedMime = getValue(preferredImage?.imageMimeType, preferredImage?.ImageMimeType) ?? getValue(item.lastImageMimeType, item.LastImageMimeType) ?? "video/mp4";
        const selectedImageId = getValue(preferredImage?.imageId, preferredImage?.ImageId) ?? timelineId;
        const src = toMediaSrc(selectedPath);
        const isVideo = looksLikeVideoAsset(selectedMime, selectedPath, selectedImageId);
        return {
          item,
          timelineId,
          src,
          isVideo,
        };
      })
      .filter((e) => !!e.src)
      .slice(0, 12);
  }, [history]);

  const desktopHistoryGridView = (
    <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
      {loadingHistory && (
        <div className="col-span-3 text-sm text-[#5C5C7A]">読み込み中...</div>
      )}
      {!loadingHistory && desktopPreviewItems.length === 0 && (
        <div className="col-span-3 text-sm text-[#5C5C7A]">履歴がありません</div>
      )}
      {!loadingHistory && desktopPreviewItems.map(({ item, timelineId, src, isVideo }) => {
        return (
          <button
            key={`grid-${timelineId}`}
            type="button"
            className="w-full aspect-square rounded-sm overflow-hidden bg-[#EDEFF6] border border-[#D9DDF2] clickable"
            onClick={() => onSelectHistory(item)}
          >
            {isVideo ? (
              <video
                className="w-full h-full object-cover bg-black"
                src={src}
                muted
                loop
                autoPlay
                playsInline
              />
            ) : (
              <img className="w-full h-full object-cover" src={src} />
            )}
          </button>
        );
      })}
    </div>
  );

  function onQuotePromptOnly() {
    const value = selectedPromptText.trim();
    if (!value) {
      toast("引用できるプロンプトがありません");
      return;
    }
    setPrompt(value);
    toast.success("プロンプトを引用しました");
    setVideoDialogOpened(false);
  }

  async function onDownloadGeneratedVideo() {
    if (!generatedVideoSrc) {
      toast("ダウンロードできる動画がありません");
      return;
    }
    try {
      const res = await fetch(generatedVideoSrc);
      if (!res.ok) throw new Error("download failed");

      const blob = await res.blob();
      const mime = (blob.type || "").toLowerCase();
      const ext = mime.includes("webm") ? "webm" : mime.includes("quicktime") ? "mov" : "mp4";
      const objectUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `dropai-video.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      const a = document.createElement("a");
      a.href = generatedVideoSrc;
      a.download = "dropai-video.mp4";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }

  async function onDeleteCurrentTimeline() {
    const timelineId = (currentTimelineId || getValue(selectedSummary?.timelineId, selectedSummary?.TimelineId) || "").trim();
    if (!timelineId || isDeletingTimeline) {
      setDeleteConfirmOpened(false);
      return;
    }

    setIsDeletingTimeline(true);
    try {
      const res = await postAsync<any>("Generation/DeleteGeminiTimelines", { timelineIds: [timelineId] });
      const code = getCode(res);
      if (code !== 1) {
        toast.error(getMessage(res));
        return;
      }

      setHistory((prev) => prev.filter((item) => (getValue(item.timelineId, item.TimelineId) ?? "") !== timelineId));
      if (currentTimelineId === timelineId) {
        setCurrentTimelineId("");
      }
      setSelectedSummary(undefined);
      setGeneratedVideoSrc(undefined);
      setRequestImages([]);
      setSelectedPromptText("");
      setVideoDialogOpened(false);
      toast.success("動画履歴を削除しました");
    } catch {
      toast.error("削除に失敗しました");
    } finally {
      setIsDeletingTimeline(false);
      setDeleteConfirmOpened(false);
    }
  }

  const timelineReferencePreviews = (() => {
    const uniqueBySrc = (items: Array<{ src: string; label: string }>) => {
      const seen = new Set<string>();
      const result: Array<{ src: string; label: string }> = [];
      for (const item of items) {
        if (seen.has(item.src)) continue;
        seen.add(item.src);
        result.push(item);
      }
      return result;
    };

    const fromTimeline = requestImages
      .map((img, idx) => {
        const path = getValue(img.imagePath, img.ImagePath);
        const mime = getValue(img.imageMimeType, img.ImageMimeType) ?? "";
        const src = toMediaSrc(path);
        if (!src) return null;
        const imageId = getValue(img.imageId, img.ImageId);
        if (!mime.startsWith("image/") || looksLikeVideoAsset(mime, path, imageId)) return null;
        return { src, label: `画像${idx + 1}` };
      })
      .filter((e): e is { src: string; label: string } => e != null);

    if (fromTimeline.length > 0) {
      return uniqueBySrc(fromTimeline).map((item, idx) => ({ ...item, label: `画像${idx + 1}` }));
    }

    const fallback = references.slice(0, 6).map((ref, idx) => ({
      src: ref.base64,
      label: `画像${idx + 1}`,
    }));
    return uniqueBySrc(fallback).map((item, idx) => ({ ...item, label: `画像${idx + 1}` }));
  })();

  return (
    <>
      <Helmet>
        <title>動画生成 - Drop AI</title>
        <meta name="description" content="Drop AI の Veo 3.1 動画生成機能です。" />
      </Helmet>
      <Toaster />
      <Modal
        zIndex={300}
        size="auto"
        opened={videoDialogOpened}
        onClose={() => setVideoDialogOpened(false)}
      >
        <div className='text-start max-h-[80vh] overflow-scroll w-full sm:w-[1080px]' style={{ paddingTop: "0px" }}>
          <div className="block sm:flex">
            <div className="w-full sm:w-[50%] items-center sm:min-h-[500px] justify-center">
              <div className="text-sm font-bold text-[#0C1F60] ml-1 mb-2">生成した動画</div>
              <div className="min-w-[100%] px-1 my-1">
                {processing && (
                  <div className="text-center min-h-[250px] sm:min-h-[450px] align-middle justify-center flex items-center">
                    <div className="flex flex-col items-center h-full">
                      <Loader type="dots" />
                      <span className="text-sm mt-2">動画生成中</span>
                      <span className="text-xs mt-1 text-black">
                        動画生成には通常1分〜3分かかります。
                        <br />
                        このモーダルウィンドウを閉じてもバックグラウンドで生成作業は途切れず進行します
                      </span>
                    </div>
                  </div>
                )}
                {!processing && !generatedVideoSrc && (
                  <div className="text-center min-h-[250px] sm:min-h-[450px] align-middle justify-center flex items-center">
                    <span className="text-sm text-[#667085]">動画のプレビューはまだありません</span>
                  </div>
                )}
                {!processing && generatedVideoSrc && (
                  <div>
                    <video className="w-full" src={generatedVideoSrc} controls playsInline />
                    <div className="mt-2 flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={onDownloadGeneratedVideo}
                        className="w-9 h-8 rounded-lg border border-[#D9DDF2] bg-white flex items-center justify-center clickable"
                        title="ダウンロード"
                      >
                        <img src="/images/icon/download.png" alt="ダウンロード" className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={onQuotePromptOnly}
                        className="w-9 h-8 rounded-lg border border-[#D9DDF2] bg-white clickable inline-flex items-center justify-center"
                        title="プロンプトのみ引用"
                      >
                        <img src="/images/icon/prompt-icon.jpg" alt="プロンプト引用" className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="w-full sm:w-[50%] ml-0 sm:ml-4 mt-4 sm:mt-0 sm:border-l sm:border-[#E6E8F0] sm:pl-10">
              <div className="mb-4 flex justify-center">
                <div className="inline-flex rounded-lg bg-[#F1F3FA] p-1">
                  <button
                    type="button"
                    className={`px-4 py-1.5 rounded text-xs font-bold ${videoDialogTab === "basic" ? "bg-white text-[#4B4FD8]" : "text-[#8C92A8]"}`}
                    onClick={() => setVideoDialogTab("basic")}
                  >
                    基本情報
                  </button>
                  <button
                    type="button"
                    className={`px-4 py-1.5 rounded text-xs font-bold ${videoDialogTab === "timeline" ? "bg-white text-[#4B4FD8]" : "text-[#8C92A8]"}`}
                    onClick={() => setVideoDialogTab("timeline")}
                  >
                    タイムライン
                  </button>
                </div>
              </div>

              {videoDialogTab === "basic" && (
                <div className="text-sm">
                  <div className="font-bold mt-4">生成日時</div>
                  <div className="py-1">{new Date(getValue(selectedSummary?.updatedDate, selectedSummary?.UpdatedDate) ?? Date.now()).toLocaleDateString("ja-JP")}</div>
                  <div className="font-bold mt-2">生成モデル</div>
                  <div className="py-1">{modelToLabel(getValue(selectedSummary?.modelName, selectedSummary?.ModelName))}</div>
                  <div className="font-bold mt-2">アスペクト比</div>
                  <div className="py-1">{getValue(selectedSummary?.aspectRatio, selectedSummary?.AspectRatio) ?? "-"}</div>
                  <div className="font-bold mt-2">解像度</div>
                  <div className="py-1">{getValue(selectedSummary?.resolution, selectedSummary?.Resolution) ?? getValue(selectedSummary?.imageSize, selectedSummary?.ImageSize) ?? "-"}</div>
                  <div className="font-bold mt-2">モード</div>
                  <div className="py-1">{modelToLabel(getValue(selectedSummary?.modelName, selectedSummary?.ModelName)).includes("Standard") ? "スタンダードモード" : "ファストモード"}</div>
                </div>
              )}

              {videoDialogTab === "timeline" && (
                <div className="text-sm">
                  <div className="rounded-lg bg-[#EFF1FB] p-3">
                    <div className="text-xs leading-relaxed whitespace-pre-wrap min-h-[64px]">
                      {selectedPromptText || "プロンプト情報はありません。"}
                    </div>
                    {timelineReferencePreviews.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {timelineReferencePreviews.map((img, idx) => {
                          return (
                            <div key={`timeline-ref-${idx}`} className="text-center">
                              <img src={img.src} className="w-12 h-12 object-cover rounded border border-[#D9DDF2]" />
                              <div className="text-[10px] text-[#4E556E] mt-1">{img.label}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {!processing && generatedVideoSrc && (
                    <div className="mt-4 rounded-lg bg-[#F5F6FA] p-3">
                      <video className="w-full" src={generatedVideoSrc} controls playsInline />
                    </div>
                  )}
                </div>
              )}

              <div
                className="mt-10 text-center text-[#ff0000] text-[12px] font-bold clickable"
                onClick={() => setDeleteConfirmOpened(true)}
              >
                この動画およびタイムラインを削除する
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        zIndex={310}
        size="sm"
        opened={deleteConfirmOpened}
        onClose={() => setDeleteConfirmOpened(false)}
        withCloseButton={false}
      >
        <div className="text-center">
          <div className="text-sm text-gray-800 whitespace-pre-line">
            削除した動画履歴は復元できません。{"\n"}削除しますか？
          </div>
          <div className="mt-5 grid grid-cols-2 gap-2">
            <button
              type="button"
              className="h-10 rounded-lg border border-[#D9DDF2] text-[#4B4FD8] clickable"
              onClick={() => setDeleteConfirmOpened(false)}
              disabled={isDeletingTimeline}
            >
              キャンセル
            </button>
            <button
              type="button"
              className="h-10 rounded-lg text-white clickable disabled:opacity-50"
              style={{ background: "linear-gradient(91.51deg, #0000F3 0.07%, #F416BF 100.07%)" }}
              onClick={onDeleteCurrentTimeline}
              disabled={isDeletingTimeline}
            >
              {isDeletingTimeline ? "削除中..." : "削除する"}
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        size="auto"
        opened={subscriptionDialogOpened}
        onClose={() => {
          closeSubscriptionDialog();
          openGenerationCreditModal();
        }}
      >
        <SubscriptionDialog
          upgradeTextVisible={true}
          onPurchase={closeSubscriptionDialog}
          onCancel={() => {
            closeSubscriptionDialog();
            openGenerationCreditModal();
          }}
          planId={subscriptionDialogProps?.planId}
        />
      </Modal>
      {generationCreditDialogProps !== undefined && (
        <Modal
          padding={0}
          size="auto"
          opened={creditDialogOpened}
          onClose={() => {
            closeCreditDialog();
            setGenerationCreditDialogProps(undefined);
          }}
          closeButtonProps={{ className: "mr-3" }}
        >
          <GenerationCreditDialog
            onPurchase={() => {
              closeCreditDialog();
              setGenerationCreditDialogProps(undefined);
            }}
            onCancel={() => {
              closeCreditDialog();
              setGenerationCreditDialogProps(undefined);
            }}
            onUpgrade={() => {
              closeCreditDialog();
              setGenerationCreditDialogProps(undefined);
              if (!subscription?.internalProductId?.startsWith("standard")) {
                const nextPlanId = getNextPlanId(subscription);
                if (!nextPlanId) return;
                setSubscriptionDialogProps({
                  planId: nextPlanId,
                  isTrial: true
                } as SubscriptionDialogProps);
                openSubscriptionDialog();
              }
            }}
          />
        </Modal>
      )}
      <Modal
        zIndex={320}
        size="sm"
        padding="lg"
        withCloseButton={false}
        opened={generatingWaitModalOpened}
        onClose={closeGeneratingWaitModal}
      >
        <div className="relative px-2 pt-2 pb-2">
          <button
            type="button"
            className="absolute right-0 top-0 p-1 clickable"
            onClick={closeGeneratingWaitModal}
            aria-label="閉じる"
          >
            <img src="/images/icon-close.svg" alt="閉じる" className="w-4 h-4" />
          </button>
          <div className="text-[20px] font-bold text-[#1B2256] leading-[1.35] text-center">
            しばしお待ちください
          </div>
          <div className="mt-2 text-[14px] text-[#2B3153] leading-relaxed">
            動画生成中に新規の動画生成はできません。動画生成が完了するまでお待ちくださいませ。
          </div>
        </div>
      </Modal>
      {videoSettingsVisible && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[400] flex items-end"
          onClick={() => setVideoSettingsVisible(false)}
        >
          <div
            className="w-full bg-white rounded-t-lg max-h-[80vh] transition-transform duration-300 ease-out transform translate-y-0 flex flex-col relative z-[401]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white border-b p-4 flex justify-between items-center rounded-t-lg">
              <h3 className="text-lg font-bold">動画生成設定</h3>
              <button onClick={() => setVideoSettingsVisible(false)} className="p-2 clickable">
                <img src="/images/icon-close.svg" className="w-[24px] h-[24px]" alt="閉じる" />
              </button>
            </div>
            <div className="p-4 pb-6 overflow-y-auto flex-1">
              <div className="mt-2">
                <label className="text-sm font-bold inline-flex items-center gap-1">
                  <img src="/images/icon/video-setting-time.png" alt="時間" className="w-4 h-4 object-contain" />
                  秒数
                </label>
                <select
                  className="w-full mt-1 border border-[#D9DDF2] rounded px-3 py-2"
                  value={durationSeconds}
                  onChange={(e) => setDurationSeconds(Number(e.target.value) as any)}
                >
                  <option value={4}>4s</option>
                  <option value={6}>6s</option>
                  <option value={8}>8s</option>
                </select>
              </div>
              <div className="mt-4">
                <label className="text-sm font-bold inline-flex items-center gap-1">
                  <img src="/images/icon/video-setting-framerate.png" alt="フレームレート" className="w-4 h-4 object-contain" />
                  フレームレート
                </label>
                <select
                  className="w-full mt-1 border border-[#D9DDF2] rounded px-3 py-2"
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value as any)}
                >
                  <option value="720p">720p</option>
                  <option value="1080p">1080p</option>
                  {mode !== "lite" && (
                    <option value="4k" disabled={!canUseResolution(currentPlanTier, mode, "4k")}>4K</option>
                  )}
                </select>
              </div>
              <div className="mt-4">
                <label className="text-sm font-bold inline-flex items-center gap-1">
                  <img src="/images/icon/video-setting-aspect.png" alt="アスペクト比" className="w-4 h-4 object-contain" />
                  アスペクト比
                </label>
                <select
                  className="w-full mt-1 border border-[#D9DDF2] rounded px-3 py-2"
                  value={aspectRatio}
                  onChange={(e) => setAspectRatio(e.target.value as any)}
                >
                  <option value="16:9">16:9</option>
                  <option value="9:16">9:16</option>
                </select>
              </div>
              <button
                type="button"
                className="mt-6 w-full py-2 rounded-lg text-white font-bold"
                style={{ background: "linear-gradient(91.51deg, #0000F3 0.07%, #F416BF 100.07%)" }}
                onClick={() => setVideoSettingsVisible(false)}
              >
                設定を閉じる
              </button>
            </div>
          </div>
        </div>
      )}
      {modelSelectorOpened && (
        <div className="fixed inset-0 z-[400] bg-black/40 flex items-end sm:items-center sm:justify-center">
          <div className="w-full sm:w-[560px] bg-white rounded-t-[28px] sm:rounded-[20px] px-4 pt-4 pb-5">
            <div className="flex items-center justify-between">
              <div className="w-8"></div>
              <div className="text-[28px] sm:text-[24px] font-bold text-[#11175B]">モデルを選択</div>
              <button className="text-[#0A0F5E] text-[34px] sm:text-[26px] leading-none" onClick={() => setModelSelectorOpened(false)}>×</button>
            </div>
            <div className="mt-5 border-t border-[#ECEEF6]"></div>
            <div className="mt-3 space-y-2">
              <button
                type="button"
                className={`w-full text-left rounded-2xl px-2 py-2 flex items-start gap-2 ${
                  canUseVideoMode(currentPlanTier, "lite") ? "hover:bg-[#F8F9FF] clickable" : "cursor-not-allowed"
                }`}
                onClick={() => { setMode("lite"); setModelSelectorOpened(false); }}
              >
                <img src="/images/icon/google.jpg" alt="Google" className="w-7 h-7 object-contain mt-1" />
                <div className="flex-1">
                  <div className="text-[18px] sm:text-[16px] text-[#262B3E]">Veo 3.1 Lite</div>
                  <div className="text-[13px] sm:text-[12px] text-[#8C92A8] mt-1">高速に動画を生成する軽量モードです。</div>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 mt-2 ${mode === "lite" ? "border-[#3190FF]" : "border-[#D5D9E8]"}`}>
                  {mode === "lite" && <div className="w-3 h-3 bg-[#3190FF] rounded-full m-[4px]"></div>}
                </div>
              </button>
              <button
                type="button"
                disabled={!canUseVideoMode(currentPlanTier, "fast")}
                className={`w-full text-left rounded-2xl px-2 py-2 flex items-start gap-2 ${
                  canUseVideoMode(currentPlanTier, "fast")
                    ? "hover:bg-[#F8F9FF] clickable"
                    : "cursor-not-allowed"
                }`}
                onClick={() => {
                  if (!canUseVideoMode(currentPlanTier, "fast")) return;
                  setMode("fast");
                  setModelSelectorOpened(false);
                }}
              >
                <img src="/images/icon/google.jpg" alt="Google" className="w-7 h-7 object-contain mt-1" />
                <div className="flex-1">
                  <div className="text-[18px] sm:text-[16px] text-[#262B3E]">Veo 3.1 Fast</div>
                  <div className="text-[13px] sm:text-[12px] text-[#8C92A8] mt-1">バランス重視で高速に動画を生成するモードです。</div>
                  {!canUseVideoMode(currentPlanTier, "fast") && (
                    <div className="text-[12px] sm:text-[11px] text-[#F416BF] mt-0.5">
                      このプランでは利用できません。Standard以上が必要です。
                    </div>
                  )}
                </div>
                <div className={`w-6 h-6 rounded-full border-2 mt-2 ${mode === "fast" ? "border-[#3190FF]" : "border-[#D5D9E8]"}`}>
                  {mode === "fast" && <div className="w-3 h-3 bg-[#3190FF] rounded-full m-[4px]"></div>}
                </div>
              </button>
              <button
                type="button"
                disabled={!canUseVideoMode(currentPlanTier, "standard")}
                className={`w-full text-left rounded-2xl px-2 py-2 flex items-start gap-2 ${
                  canUseVideoMode(currentPlanTier, "standard")
                    ? "hover:bg-[#F8F9FF] clickable"
                    : "cursor-not-allowed"
                }`}
                onClick={() => {
                  if (!canUseVideoMode(currentPlanTier, "standard")) return;
                  setMode("standard");
                  setModelSelectorOpened(false);
                }}
              >
                <img src="/images/icon/google.jpg" alt="Google" className="w-7 h-7 object-contain mt-1" />
                <div className="flex-1">
                  <div className="text-[18px] sm:text-[16px] text-[#262B3E]">Veo 3.1 Standard</div>
                  <div className="text-[13px] sm:text-[12px] text-[#8C92A8] mt-1">品質重視で動画を生成するスタンダードモードです。</div>
                  {!canUseVideoMode(currentPlanTier, "standard") && (
                    <div className="text-[12px] sm:text-[11px] text-[#F416BF] mt-0.5">
                      このプランでは利用できません。Standard以上が必要です。
                    </div>
                  )}
                </div>
                <div className={`w-6 h-6 rounded-full border-2 mt-2 ${mode === "standard" ? "border-[#3190FF]" : "border-[#D5D9E8]"}`}>
                  {mode === "standard" && <div className="w-3 h-3 bg-[#3190FF] rounded-full m-[4px]"></div>}
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
      {desktopSettingPicker && (
        <div className="hidden sm:flex fixed inset-0 z-[400] bg-black/40 items-center justify-center" onClick={() => setDesktopSettingPicker(null)}>
          <div className="w-[420px] bg-white rounded-[18px] px-4 pt-4 pb-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="w-6"></div>
              <div className="text-[20px] font-bold text-[#11175B]">
                {desktopSettingPicker === "resolution" ? "解像度を選択" : desktopSettingPicker === "duration" ? "秒数を選択" : "アスペクト比を選択"}
              </div>
              <button className="text-[#0A0F5E] text-[28px] leading-none" onClick={() => setDesktopSettingPicker(null)}>×</button>
            </div>
            <div className="mt-4 border-t border-[#ECEEF6]"></div>
            <div className="mt-3 space-y-2">
              {desktopSettingPicker === "resolution" && availableResolutionOptions.map((value) => {
                const enabled = canUseResolution(currentPlanTier, mode, value);
                const selected = resolution === value;
                return (
                  <button
                    key={`resolution-${value}`}
                    type="button"
                    disabled={!enabled}
                    className={`w-full text-left rounded-2xl px-3 py-2 border ${
                      selected ? "border-[#5A66FF] bg-[#F3F5FF]" : "border-[#E2E5F3] bg-white"
                    } ${enabled ? "clickable" : "opacity-50 cursor-not-allowed"}`}
                    onClick={() => {
                      if (!enabled) return;
                      setResolution(value);
                      setDesktopSettingPicker(null);
                    }}
                  >
                    <span className="text-[16px] text-[#262B3E]">{value.toUpperCase()}</span>
                  </button>
                );
              })}
              {desktopSettingPicker === "duration" && availableDurationOptions.map((value) => {
                const selected = durationSeconds === value;
                return (
                  <button
                    key={`duration-${value}`}
                    type="button"
                    className={`w-full text-left rounded-2xl px-3 py-2 border clickable ${
                      selected ? "border-[#5A66FF] bg-[#F3F5FF]" : "border-[#E2E5F3] bg-white"
                    }`}
                    onClick={() => {
                      setDurationSeconds(value as 4 | 6 | 8);
                      setDesktopSettingPicker(null);
                    }}
                  >
                    <span className="text-[16px] text-[#262B3E]">{value}s</span>
                  </button>
                );
              })}
              {desktopSettingPicker === "aspectRatio" && (["16:9", "9:16"] as const).map((value) => {
                const selected = aspectRatio === value;
                return (
                  <button
                    key={`aspect-ratio-${value}`}
                    type="button"
                    className={`w-full text-left rounded-2xl px-3 py-2 border clickable ${
                      selected ? "border-[#5A66FF] bg-[#F3F5FF]" : "border-[#E2E5F3] bg-white"
                    }`}
                    onClick={() => {
                      setAspectRatio(value);
                      setDesktopSettingPicker(null);
                    }}
                  >
                    <span className="text-[16px] text-[#262B3E]">{value}</span>
                  </button>
                );
              })}
            </div>
            {desktopSettingPicker === "duration" && requiresFixedEightSeconds && (
              <div className="mt-3 text-[12px] text-[#8C92A8]">この条件では8秒固定です。</div>
            )}
          </div>
        </div>
      )}
      <div className="block sm:flex sm:pt-4 sm:ml-2 min-w-0" style={{ backgroundColor: "#eaeaea80" }}>
        <div id="center-cell" className="sm:ml-4 w-full sm:flex-1 sm:min-w-0 sm:rounded-md mb-0 sm:mb-12 pb-24 sm:pb-8 bg-white">
          <div className="mt-0 sm:mt-4 px-4 sm:px-4">
            {screenWidth < 640 && (
              <div className="mb-3 flex justify-center">
                <div className="inline-flex items-center gap-1 rounded-lg border border-[#E3E6F2] bg-[#EEF0F7] p-1">
                  <button
                    type="button"
                    className="h-7 px-3 rounded-md text-[12px] text-[#8E95AE] inline-flex items-center gap-1"
                    onClick={() => navigate("/image-generation")}
                  >
                    <img src={iconImageAdd} alt="画像生成" className="w-3.5 h-3.5 object-contain" />
                    <span>画像</span>
                  </button>
                  <button
                    type="button"
                    className="h-7 px-3 rounded-md bg-white text-[12px] text-[#2B315D] shadow-[0_1px_2px_rgba(0,0,0,0.08)] inline-flex items-center gap-1"
                  >
                    <img src="/images/icon/camera.jpg" alt="動画生成" className="w-3.5 h-3.5 object-contain rounded-sm" />
                    <span>動画</span>
                  </button>
                </div>
              </div>
            )}
            <div className="hidden sm:block text-center text-[30px] font-bold gradation-text">自分だけのAI動画を生成しよう</div>

            <div className="mt-5 sm:mt-7 relative">
              <button
                type="button"
                className="hidden sm:flex absolute right-0 -top-[42px] clickable items-center justify-center w-9 h-9 rounded-lg bg-white border border-gray-200"
                aria-label="プロンプトをクリア"
                onClick={() => {
                  setPrompt("");
                  closeMentionSuggest();
                  setMentionButtonOpened(false);
                }}
              >
                <img src="/images/icon-close.svg" alt="クリア" className="w-[14px] h-[14px]" />
              </button>
              <div className="relative border rounded-2xl border-[#7E71FF] bg-white p-3 sm:p-4 pb-5">
              <div className="flex items-center flex-nowrap gap-2 overflow-x-auto sm:overflow-visible text-xs text-[#6F7794]">
                <div className="flex items-center shrink-0">
                  <div ref={firstFrameWrapRef} className="relative">
                    <button
                      type="button"
                      className="h-8 px-2 rounded-lg border border-[#D9DDF2] bg-white flex items-center clickable"
                      onClick={() => {
                        if (frameCandidates.length > 0) {
                          setFramePickerTarget((prev) => (prev === "first" ? null : "first"));
                          return;
                        }
                        firstFrameInputRef.current?.click();
                      }}
                      title="最初のフレーム画像を追加"
                    >
                      <span className="text-[#6F7794]">最初のフレーム</span>
                      {selectedFirstImage && (
                        <img src={selectedFirstImage.base64} alt="最初のフレーム" className="ml-1 w-6 h-6 rounded object-cover" />
                      )}
                    </button>
                    {selectedFirstImage && (
                      <button
                        type="button"
                        className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-white border border-gray-300 text-[10px] leading-none text-[#5B5F7A] flex items-center justify-center clickable"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFirstFrameId("");
                        }}
                        title="最初のフレームをクリア"
                      >
                        ×
                      </button>
                    )}
                    {framePickerTarget === "first" && frameCandidates.length > 0 && (
                      <div className="absolute z-20 left-0 top-full mt-1 w-[220px] max-h-[200px] overflow-y-auto rounded-lg border border-[#D9DDF2] bg-white shadow-[0_8px_20px_rgba(0,0,0,0.12)]">
                        {frameCandidates.map((c) => (
                          <button
                            key={`first-frame-candidate-${c.id}`}
                            type="button"
                            className="w-full text-left px-2 py-1.5 text-sm hover:bg-[#F7F8FC] flex items-center gap-2"
                            onClick={() => {
                              setFirstFrameId(c.id);
                              setFramePickerTarget(null);
                            }}
                          >
                            <img src={c.src} className="w-8 h-8 rounded object-cover border border-[#D9DDF2]" />
                            <span className="text-[#11175B]">@{c.token}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    ref={firstFrameInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onUploadFrameImage(e.target.files, "first")}
                  />
                </div>
                <div className="flex items-center shrink-0 ml-0 sm:ml-2">
                  <div ref={lastFrameWrapRef} className="relative">
                    <button
                      type="button"
                      className="h-8 px-2 rounded-lg border border-[#D9DDF2] bg-white flex items-center clickable"
                      onClick={() => {
                        if (frameCandidates.length > 0) {
                          setFramePickerTarget((prev) => (prev === "last" ? null : "last"));
                          return;
                        }
                        lastFrameInputRef.current?.click();
                      }}
                      title="最後のフレーム画像を追加"
                    >
                      <span className="text-[#6F7794]">最後のフレーム</span>
                      {selectedLastImage && (
                        <img src={selectedLastImage.base64} alt="最後のフレーム" className="ml-1 w-6 h-6 rounded object-cover" />
                      )}
                    </button>
                    {selectedLastImage && (
                      <button
                        type="button"
                        className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-white border border-gray-300 text-[10px] leading-none text-[#5B5F7A] flex items-center justify-center clickable"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLastFrameId("");
                        }}
                        title="最後のフレームをクリア"
                      >
                        ×
                      </button>
                    )}
                    {framePickerTarget === "last" && frameCandidates.length > 0 && (
                      <div className="absolute z-20 left-0 top-full mt-1 w-[220px] max-h-[200px] overflow-y-auto rounded-lg border border-[#D9DDF2] bg-white shadow-[0_8px_20px_rgba(0,0,0,0.12)]">
                        {frameCandidates.map((c) => (
                          <button
                            key={`last-frame-candidate-${c.id}`}
                            type="button"
                            className="w-full text-left px-2 py-1.5 text-sm hover:bg-[#F7F8FC] flex items-center gap-2"
                            onClick={() => {
                              setLastFrameId(c.id);
                              setFramePickerTarget(null);
                            }}
                          >
                            <img src={c.src} className="w-8 h-8 rounded object-cover border border-[#D9DDF2]" />
                            <span className="text-[#11175B]">@{c.token}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    ref={lastFrameInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onUploadFrameImage(e.target.files, "last")}
                  />
                </div>
              </div>

              <div ref={promptInputWrapRef} className="relative mt-3">
                <textarea
                  ref={promptTextareaRef}
                  className={`relative z-10 w-full border-0 outline-none focus:ring-0 resize-none overflow-auto text-[18px] placeholder:text-[#B7BDCF] bg-transparent ${
                    prompt.length > 0 ? "text-transparent caret-[#1F2352]" : "text-[#1F2352]"
                  }`}
                  style={{ minHeight: `${promptInputMinHeight}px` }}
                  placeholder="動画の指示を入力。「@」を押すと参照画像指定ができます。最初と最後のフレームを参照画像で指定できます。"
                  value={prompt}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPrompt(value);
                    const caretPos = e.target.selectionStart ?? value.length;
                    updateMentionState(value, caretPos);
                  }}
                  onClick={(e) => {
                    const ta = e.currentTarget;
                    const caretPos = ta.selectionStart ?? prompt.length;
                    updateMentionState(prompt, caretPos);
                  }}
                  onKeyDown={(e) => {
                    if (mentionStart == null || filteredMentionCandidates.length === 0) return;
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setMentionActiveIndex((prev) => (prev + 1) % filteredMentionCandidates.length);
                      return;
                    }
                    if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setMentionActiveIndex((prev) => (prev - 1 + filteredMentionCandidates.length) % filteredMentionCandidates.length);
                      return;
                    }
                    if (e.key === "Enter" || e.key === "Tab") {
                      e.preventDefault();
                      const target = filteredMentionCandidates[Math.max(0, mentionActiveIndex)];
                      if (target) insertMention(target.token);
                      return;
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      closeMentionSuggest();
                    }
                  }}
                  onKeyUp={(e) => {
                    if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === "Tab" || e.key === "Escape") {
                      return;
                    }
                    const ta = e.currentTarget;
                    const caretPos = ta.selectionStart ?? prompt.length;
                    updateMentionState(ta.value, caretPos);
                  }}
                  onScroll={(e) => {
                    syncPromptHighlightScroll();
                    const ta = e.currentTarget;
                    updateMentionPopupPosition(ta.selectionStart ?? prompt.length);
                  }}
                />
                <div
                  ref={promptHighlightRef}
                  className="pointer-events-none absolute inset-0 overflow-hidden text-[18px] leading-[1.5] text-[#1F2352] whitespace-pre-wrap break-words"
                >
                  {renderPromptHighlighted(prompt)}
                </div>
                {mentionStart != null && filteredMentionCandidates.length > 0 && (
                  <div
                    className="absolute z-20 w-[240px] max-h-[180px] overflow-y-auto rounded-lg border border-[#D9DDF2] bg-white shadow-[0_8px_20px_rgba(0,0,0,0.12)]"
                    style={{ left: mentionPopupPos.left, top: mentionPopupPos.top }}
                  >
                    {filteredMentionCandidates.map((candidate, idx) => (
                      <button
                        key={`mention-candidate-${candidate.id}`}
                        type="button"
                        className={`w-full text-left px-3 py-2 text-sm ${
                          idx === mentionActiveIndex ? "bg-[#EEF1FF] text-[#11175B]" : "text-[#3E466A] hover:bg-[#F7F8FC]"
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          insertMention(candidate.token);
                        }}
                    >
                        <span className="inline-flex items-center gap-2">
                          {candidate.src && (
                            <img src={candidate.src} className="w-6 h-6 rounded object-cover border border-[#D9DDF2]" />
                          )}
                          <span>@{candidate.token}</span>
                        </span>
                    </button>
                  ))}
                </div>
                )}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input
                  ref={referenceInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onUploadReferenceImages(e.target.files)}
                />
                <button
                  type="button"
                  className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center clickable relative overflow-hidden"
                  onClick={() => referenceInputRef.current?.click()}
                  title="参照画像を追加"
                >
                  <img src={iconImageAdd} alt="画像追加" className="w-full h-full p-[6px]" />
                </button>
                <button
                  type="button"
                  onClick={() => setVideoSettingsVisible(true)}
                  className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center clickable sm:hidden"
                  title="動画設定"
                >
                  <img src={iconAdjust} alt="動画設定" className="w-4 h-4" />
                </button>
                {buildVideoModelSelector(screenWidth < 640)}
                <button
                  type="button"
                  className="hidden sm:inline-flex h-9 px-3 rounded-2xl border border-[#D9DDF2] bg-white text-sm text-[#1F2352] items-center clickable"
                  onClick={() => setDesktopSettingPicker("resolution")}
                  title="解像度"
                >
                  <img src="/images/icon/video-setting-size.png" alt="映像サイズ" className="w-4 h-4 mr-1 object-contain" />
                  {resolution.toUpperCase()} <span className="ml-1 text-[#7C82A3]">{">"}</span>
                </button>
                <button
                  type="button"
                  className="hidden sm:inline-flex h-9 px-3 rounded-2xl border border-[#D9DDF2] bg-white text-sm text-[#1F2352] items-center clickable"
                  onClick={() => setDesktopSettingPicker("duration")}
                  title={requiresFixedEightSeconds ? "この条件では8秒固定です（設定を開く）" : "秒数設定"}
                >
                  <img src="/images/icon/video-setting-time.png" alt="時間" className="w-4 h-4 mr-1 object-contain" />
                  {effectiveDuration}s <span className="ml-1 text-[#7C82A3]">{">"}</span>
                </button>
                <button
                  type="button"
                  className="hidden sm:inline-flex h-9 px-3 rounded-2xl border border-[#D9DDF2] bg-white text-sm text-[#1F2352] items-center clickable"
                  onClick={() => setDesktopSettingPicker("aspectRatio")}
                  title="アスペクト比"
                >
                  <img src="/images/icon/video-setting-aspect.png" alt="アスペクト比" className="w-4 h-4 mr-1 object-contain" />
                  {aspectRatio} <span className="ml-1 text-[#7C82A3]">{">"}</span>
                </button>
                <div ref={mentionButtonWrapRef} className="relative hidden sm:block">
                  <button
                    type="button"
                    className="inline-flex h-9 px-3 rounded-2xl border border-[#D9DDF2] bg-white text-sm text-[#1F2352] items-center clickable"
                    onClick={() => setMentionButtonOpened((prev) => !prev)}
                    title="参照画像メンションを追加"
                  >
                    @
                  </button>
                  {mentionButtonOpened && (
                    <div className="absolute z-20 right-0 top-full mt-1 w-[220px] max-h-[220px] overflow-y-auto rounded-lg border border-[#D9DDF2] bg-white shadow-[0_8px_20px_rgba(0,0,0,0.12)]">
                      {mentionCandidates.length === 0 && (
                        <div className="px-3 py-2 text-xs text-[#6F7794]">参照画像がありません</div>
                      )}
                      {mentionCandidates.map((candidate) => (
                        <button
                          key={`mention-button-candidate-${candidate.id}`}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm text-[#3E466A] hover:bg-[#F7F8FC] flex items-center gap-2"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            insertMention(candidate.token, true);
                          }}
                        >
                          {candidate.src && (
                            <img src={candidate.src} className="w-6 h-6 rounded object-cover border border-[#D9DDF2]" />
                          )}
                          <span>@{candidate.token}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="ml-auto flex items-center gap-2">
                  <div className="flex items-center text-[14px] leading-none pr-[2px] shrink-0" style={{ fontWeight: 400 }}>
                    <img src="/images/icon/icons8-lightning.png" alt="消費クレジット" className="w-[14px] h-[14px] mr-[2px]" />
                    <span>{currentConsumingCredit ?? "-"}</span>
                  </div>
                  <button
                    type="button"
                    className="w-8 h-8 rounded-xl text-white font-bold flex items-center justify-center clickable disabled:opacity-50 shrink-0"
                    style={{ background: "linear-gradient(91.51deg, #0000F3 0.07%, #F416BF 100.07%)" }}
                    onClick={onGenerateVideo}
                    title="AI生成"
                  >
                    <img src="/images/flare.png" alt="生成" className="w-4 h-4" />
                  </button>
                </div>
	              </div>
	              {buildSelectedReferenceImageRow()}
	              <div
	                className="absolute left-4 right-4 bottom-1 h-3 cursor-row-resize"
	                onMouseDown={(event) => {
	                  if (event.button !== 0) return;
	                  event.preventDefault();
	                  setPromptResizeState({ startY: event.clientY, startHeight: promptInputMinHeight });
	                }}
	              >
	                <div className="mx-auto mt-[7px] h-[2px] w-14 rounded-full bg-[#E1DFFF]" />
	              </div>
	            </div>
	            </div>

            <div className="mt-10">
              <div className="font-bold text-lg text-[#0C1F60]">動画プロンプトアイデア</div>
              <div className="mt-3 flex flex-wrap gap-3">
                {promptIdeas.map((idea, index) => {
                  const label = getValue(idea.name, idea.Name) ?? `アイデア${index + 1}`;
                  const promptText = getValue(idea.prompt, idea.Prompt) ?? label;
                  const key = `${getValue(idea.uid, idea.Uid) ?? `fallback-${index}`}-${label}`;

                  return (
                    <button
                      key={key}
                      type="button"
                      className="px-4 py-2 rounded-2xl border border-[#D9DDF2] text-[#12206A] text-sm font-bold bg-white"
                      onClick={() => setPrompt(promptText)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            {isLoggedIn === true && (
              <div className="sm:hidden mt-6">
                <a href={`${getSkyFlagUrl()}`} target="_blank" onClick={() => {
                  const url = getSkyFlagUrl();
                  console.log('[AppBanner] Click (video-mobile):', url);
                }}>
                  <img src="/images/skyflag-banner2.png" className="w-full border-0" style={{ border: 'none', outline: 'none' }}></img>
                </a>
              </div>
            )}

          </div>
        </div>

        <div id="generation-history" className="hidden sm:block w-[420px] min-w-[420px] ml-2 mr-4 mt-0 box-border mb-[320px] sm:mb-6">
          <div className="rounded-xl border border-[#E1E4EF] bg-white p-4">
            <div className="flex items-center justify-between mb-6">
              <span className="font-bold text-lg text-[#101B63]">動画の生成履歴</span>
              <a href="/generation-history" className="flex items-center text-sm text-gray-500">もっと見る <img src="/images/icon/icon-right.svg" className="w-[16px] h-[16px]"></img></a>
            </div>
            {desktopHistoryGridView}
          </div>
          {isLoggedIn === true && (
            <div className="mt-6">
              <a href={`${getSkyFlagUrl()}`} target="_blank" onClick={() => {
                const url = getSkyFlagUrl();
                console.log('[AppBanner] Click (video-right-col):', url);
              }}>
                <img src="/images/skyflag-banner2.png" className="w-full"></img>
              </a>
            </div>
          )}
        </div>
      </div>
      {screenWidth < 640 && <FooterMenu />}
    </>
  );
};
